import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import WorkspaceLoader from '../components/WorkspaceLoader';


const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { user, refreshUser } = auth;
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const verificationAttempted = React.useRef(false);

  const verifyEmail = useCallback(async (token) => {
    try {
      const response = await apiClient.get(`/auth/verify-email`, {
        params: { token }
      });
      
      // Check if already verified
      const alreadyVerified = response.data.already_verified;
      const successMessage = alreadyVerified 
        ? 'Your email is already verified!'
        : 'Email verified successfully!';
      
      setStatus('success');
      setMessage(response.data.message || successMessage);
      
      // Refresh user data to get updated email_verified status
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success(successMessage);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, alreadyVerified ? 1500 : 2000);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      
      // Check if error message indicates already verified (fallback for old backend)
      if (errorDetail.toLowerCase().includes('already verified')) {
        setStatus('success');
        setMessage('Your email is already verified!');
        toast.success('Email already verified!');
        
        // Refresh user and redirect
        if (refreshUser) {
          await refreshUser();
        }
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // Actual error - token invalid or expired
        setStatus('error');
        setMessage(errorDetail || 'Verification failed. The token may be invalid or expired.');
        toast.error('Email verification failed. Please try again or request a new link.');
      }
    }
  }, [refreshUser, navigate]);

  useEffect(() => {
    try {
      if (window.location.hostname.includes('onrender.com')) {
        window.location.replace(`https://interguide.app${window.location.pathname}${window.location.search}`);
        return;
      }

      // Prevent double verification attempts (React StrictMode or re-renders)
      if (verificationAttempted.current) {
        return;
      }

      const token = searchParams.get('token');
      if (token) {
        verificationAttempted.current = true;
        verifyEmail(token);
      } else {
        setStatus('error');
        setMessage('No verification token provided.');
      }
    } catch (error) {
      console.error('Email verification initialization error:', error);
      setStatus('error');
      setMessage('An error occurred during email verification. Please try again.');
      toast.error('Email verification failed. Please try again.');
    }
  }, [searchParams, verifyEmail]);

  const handleResend = async () => {
    if (!user) {
      toast.error('Please log in to resend verification email');
      navigate('/login');
      return;
    }

    setResending(true);
    try {
      await apiClient.post(`/auth/resend-verification`, {});
      toast.success('Verification email sent! Please check your inbox.');
      setMessage('Verification email sent. Please check your inbox.');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send verification email';
      toast.error(errorMsg);
      setMessage(errorMsg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-black dark:to-slate-900 p-6">
      <Card variant="glass" className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'verifying' && (
            <>
              <WorkspaceLoader size={160} />
              <CardTitle>Verifying Email</CardTitle>
              <CardDescription>Please wait while we verify your email address...</CardDescription>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-blue-600" />
              <CardTitle>Email Verified!</CardTitle>
              <CardDescription>Your email has been successfully verified.</CardDescription>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <CardTitle>Verification Failed</CardTitle>
              <CardDescription>{message}</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'error' && (
            <>
              <p className="text-sm text-slate-600 text-center">
                If your verification link has expired, you can request a new one.
              </p>
              {user ? (
                <Button onClick={handleResend} disabled={resending} className="w-full">
                  {resending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button onClick={() => navigate('/login')} className="w-full">
                    Log In
                  </Button>
                  <Button onClick={() => navigate('/signup')} variant="outline" className="w-full">
                    Sign Up
                  </Button>
                </div>
              )}
            </>
          )}
          {status === 'success' && (
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;